import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  getCategoryList,
  getMenuList,
} from '../../../api/services/systemService';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isTokenExpired, navigateToLogin } from '../../../utils';

const SectionContainer = styled.section`
  margin: 0 auto;
  text-align: center;
  width: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Desktop/Tablet Grid Container
const CategoryGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  justify-content: space-between;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
    width: 95%;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

// Mobile Grid Container
const MobileGrid = styled.div`
  display: none;
  width: 100%;

  @media (max-width: 480px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    width: 100%;
    padding: 0 5px;
  }
`;

const CategoryCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: ${(props) => props.theme.colors.primaryText};
  box-sizing: border-box;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    width: 100%;
    align-items: center;
    box-sizing: border-box;
    padding: 10px;
  }
`;

const CategoryImage = styled.img`
  background-color: #f8f9fa;
  width: 100%;
  height: 220px;
  margin-bottom: 12px;
  max-width: 100%;
  object-fit: cover;
  min-height: 220px;

  @media (max-width: 768px) {
    height: 140px;
    min-height: 140px;
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 160px;
    min-height: 160px;
    margin-bottom: 8px;
    max-width: 100%;
  }
`;

const CategoryTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  text-align: center;
  color: #333;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

export default function CategoryView() {
  const userInfo = useSelector((state) => state.userInfo);
  const navigate = useNavigate();

  const [categoryList, setCategoryList] = useState([]);

  useEffect(() => {
    getDbCategoryList();
    // 로그인/로그아웃 시 webuser 별 숨김 카테고리 반영
  }, [userInfo.userId]);

  const getDbCategoryList = async () => {
    const result = await getCategoryList(userInfo.brandCode);
    let list = result.categoryList || [];

    // webuser 별로 숨긴 카테고리의 카드는 제외
    // 전체 메뉴와 webuser 메뉴의 차집합 = 숨긴 카테고리. 카드 URL/제목과 부분일치 비교
    // (카드 제목이 단수형(Rice Cooker)이어도 카테고리(Rice Cookers)와 매칭되도록)
    if (userInfo.userId != 0) {
      try {
        const slugify = (value) =>
          String(value || '')
            .toLowerCase()
            .replace(/[^a-z\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');

        const keysOf = (menuList) =>
          (menuList || [])
            .flatMap((cat) => [slugify(cat.cateNm), slugify(cat.urlKey)])
            .filter(Boolean);

        const [fullMenuResult, userMenuResult] = await Promise.all([
          getMenuList(),
          getMenuList(userInfo.userId),
        ]);
        const visibleKeys = new Set(keysOf(userMenuResult.menuList));
        const hiddenKeys = keysOf(fullMenuResult.menuList).filter(
          (key) => !visibleKeys.has(key),
        );

        list = list.filter((category) => {
          const match = String(category.url || '').match(/category=([^&]+)/i);
          const key = match
            ? slugify(decodeURIComponent(match[1]))
            : slugify(category.title);
          if (!key) return true;
          return !hiddenKeys.some(
            (hidden) =>
              hidden === key ||
              hidden.startsWith(key) ||
              key.startsWith(hidden),
          );
        });
      } catch (e) {
        // 메뉴 조회 실패 시 기존 동작(전체 표시) 유지
      }
    }

    setCategoryList(list);
  };

  const handleCategoryClick = (categoryUrl) => {
    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) {
      navigateToLogin(navigate, categoryUrl.split('#')[1] || categoryUrl);
    } else {
      // Save flag to scroll back to category section when returning
      sessionStorage.setItem('scrollToCategoryView', 'true');
      // Extract the path after the # from the URL
      const path = categoryUrl.split('#')[1] || categoryUrl;
      navigate(path);
    }
  };

  return (
    <SectionContainer id='category-section'>
      {/* Desktop Grid */}
      <CategoryGrid>
        {categoryList.map((category) => (
          <CategoryCard
            key={category.title}
            onClick={() => handleCategoryClick(category.url)}
          >
            <CategoryImage
              src={`${process.env.REACT_APP_FILE_UPLOAD_URL}${category.filepath1}`}
              alt={category.title}
            />
            <CategoryTitle>{category.title}</CategoryTitle>
          </CategoryCard>
        ))}
      </CategoryGrid>

      {/* Mobile Grid */}
      <MobileGrid>
        {categoryList.map((category) => (
          <CategoryCard
            key={category.title}
            onClick={() => handleCategoryClick(category.url)}
          >
            <CategoryImage
              src={`${process.env.REACT_APP_FILE_UPLOAD_URL}${category.filepath1}`}
              alt={category.title}
            />
            <CategoryTitle>{category.title}</CategoryTitle>
          </CategoryCard>
        ))}
      </MobileGrid>
    </SectionContainer>
  );
}
